import classNames from 'classnames';
import React from 'react';
import type { MouseEvent } from 'react';

import { PROCESSING_STATUS } from 'constants/MediaConstants';
import type { VideoMedia as VideoMediaType } from 'modules/medias/types/video-media';
import ProcessingErrorIcon from 'ui/base/icons/error-video-icon.svg';
import ProcessingIcon from 'ui/base/icons/loading-video-icon.svg';

import type { MediaGalleryActions, MediaListItem } from '../../types';
import Actions from '../Actions';
import { PlayIconWrapper, ProcessingWrapper, VideoThumbnail, VideoWrapper } from '../UI';

import ImageMedia from './Image';
import Restrict from './Restrict';

type VideoMediaProps = {
  media: MediaListItem<VideoMediaType>;
} & MediaGalleryActions;

const VideoMedia: React.FC<VideoMediaProps> = ({ media, onRestrict, disabledOnRestrict, ...actions }) => {
  const { isDuplicated, thumbnailPath, path, processingStatus } = media.data;

  const hasProcessingError = processingStatus === PROCESSING_STATUS.ERROR;
  const showRestrict = !isDuplicated && onRestrict && !hasProcessingError;
  const isProcessing = processingStatus && processingStatus !== PROCESSING_STATUS.PROCESSED;

  // When we have a thumbnail, it is either a blob or an URL to a file
  // If it's a blob, we just uploaded it and we can't use the ImageViewer as it is a video
  // If it's an URL, we can use the ImageViewer as it is a thumbnail generated by the server
  const useImageViewer = Boolean(!isProcessing && thumbnailPath && !thumbnailPath.startsWith('blob:'));

  const handleOnClick = React.useCallback(
    (event: MouseEvent<HTMLVideoElement>) => {
      event.preventDefault();
      event.stopPropagation();
      actions.onPreview?.(media);
    },
    [actions, media]
  );

  return (
    <>
      {useImageViewer ? (
        <ImageMedia media={media} onRestrict={onRestrict} disabledOnRestrict={disabledOnRestrict} {...actions} />
      ) : (
        <React.Fragment>
          <VideoWrapper>
            {isProcessing && (
              <ProcessingWrapper className={classNames({ error: hasProcessingError })}>
                {!hasProcessingError && <ProcessingIcon />}
                {hasProcessingError && <ProcessingErrorIcon />}
              </ProcessingWrapper>
            )}
            {!isProcessing && (
              <React.Fragment>
                <VideoThumbnail src={path} onClick={handleOnClick} data-cy="media-gallery-tile" />
                <PlayIconWrapper>
                  <i className="fb-icon--play" />
                </PlayIconWrapper>
              </React.Fragment>
            )}
          </VideoWrapper>
          <Actions media={media} {...actions} />
        </React.Fragment>
      )}
      {showRestrict && <Restrict media={media} onRestrict={onRestrict} disabled={disabledOnRestrict} />}
    </>
  );
};

export default VideoMedia;
